import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, contentChild, ElementRef, inject, input, model, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import {
  MatrixCellDefDirective,
  MatrixExpandedDefDirective,
  MatrixFooterDefDirective,
  MatrixHeaderDefDirective,
} from './matrix-grid.directive';
import { IMatrixGridColumn, IMatrixGridRow, IMatrixGridSort } from './matrix-grid.interface';

const COLLAPSED_COLUMN_WIDTH = '36px';
/** Takes the width the columns leave, so lines, header and footer span the whole table. */
const FILLER_COLUMN_WIDTH = 'minmax(0, 1fr)';

/**
 * Rows × columns table on a CSS grid: sticky leading columns, horizontal scroll, expandable rows,
 * collapsible columns and a row + column highlight that follows hover and keyboard focus.
 * Header, cell, expanded and footer content come from the feature through template directives; collapsed columns
 * keep their cell and footer templates (context `collapsed: true`) so the feature can show a compact summary.
 */
@Component({
  imports: [IconComponent, MatTooltipModule, NgTemplateOutlet, NgxSkeletonLoaderComponent, TranslateModule],
  selector: 'app-matrix-grid',
  styleUrl: './matrix-grid.component.scss',
  templateUrl: './matrix-grid.component.html',
})
export class MatrixGridComponent<TRow extends IMatrixGridRow, TColumn extends IMatrixGridColumn> {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly columns = input.required<TColumn[]>();
  public readonly rows = input.required<TRow[]>();
  public readonly collapsedColumnIds = model<string[]>([]);
  public readonly expandedRowIds = model<string[]>([]);
  public readonly loading = input<boolean>(false);
  public readonly skeletonRows = input<number>(5);
  public readonly ariaLabel = input<string>('');
  public readonly sort = input<IMatrixGridSort | null>(null);

  public readonly headerDef = contentChild.required(MatrixHeaderDefDirective<TColumn>);
  public readonly cellDef = contentChild.required(MatrixCellDefDirective<TRow, TColumn>);
  public readonly expandedDef = contentChild(MatrixExpandedDefDirective<TRow, TColumn>);
  public readonly footerDef = contentChild(MatrixFooterDefDirective<TColumn>);

  public readonly hoveredRowId = signal<string | null>(null);
  public readonly hoveredColumnId = signal<string | null>(null);

  public readonly collapsedIds = computed(() => {
    const known = new Set(this.columns().map((column) => column.id));

    return new Set(this.collapsedColumnIds().filter((id) => known.has(id)));
  });

  public readonly expandedIds = computed(() => new Set(this.expandedRowIds()));

  public readonly gridTemplateColumns = computed(() =>
    [
      ...this.columns().map((column) => (this.collapsedIds().has(column.id) ? COLLAPSED_COLUMN_WIDTH : column.width)),
      FILLER_COLUMN_WIDTH,
    ].join(' '),
  );

  /** `left` offset per sticky column: the sum of the sticky columns before it. */
  public readonly stickyLeft = computed(() => {
    const offsets = new Map<string, string>();
    const widths: string[] = [];

    for (const column of this.columns()) {
      if (!column.sticky) {
        continue;
      }

      offsets.set(column.id, widths.length > 1 ? `calc(${widths.join(' + ')})` : (widths[0] ?? '0px'));
      widths.push(column.width);
    }

    return offsets;
  });

  public readonly lastStickyId = computed(
    () =>
      this.columns()
        .filter((column) => column.sticky)
        .at(-1)?.id ?? null,
  );

  public readonly skeletonRowIds = computed(() => Array.from({ length: this.skeletonRows() }, (_, index) => index));

  public getAriaSort(columnId: string): 'ascending' | 'descending' | null {
    const sort = this.sort();

    if (sort?.columnId !== columnId) {
      return null;
    }

    return sort.direction === 'asc' ? 'ascending' : 'descending';
  }

  public isCollapsed(columnId: string): boolean {
    return this.collapsedIds().has(columnId);
  }

  public isRowExpanded(rowId: string): boolean {
    return this.expandedIds().has(rowId);
  }

  public collapseColumn(columnId: string): void {
    this.collapsedColumnIds.set([...this.collapsedIds(), columnId].filter(this.unique));
  }

  public expandColumn(columnId: string): void {
    this.collapsedColumnIds.set([...this.collapsedIds()].filter((id) => id !== columnId));
    this.focusHeader(columnId);
  }

  /** Focus goes to the first column shown again. */
  public showAllColumns(): void {
    const firstHidden = this.columns().find((column) => this.isCollapsed(column.id));

    this.collapsedColumnIds.set([]);

    if (firstHidden) {
      this.focusHeader(firstHidden.id);
    }
  }

  /** Collapses every other collapsible column. */
  public focusColumn(columnId: string): void {
    this.collapsedColumnIds.set(
      this.columns()
        .filter((column) => column.collapsible && column.id !== columnId)
        .map((column) => column.id),
    );
  }

  public toggleRow(rowId: string): void {
    const expanded = this.expandedIds();

    this.expandedRowIds.set(expanded.has(rowId) ? [...expanded].filter((id) => id !== rowId) : [...expanded, rowId]);
  }

  public expandAllRows(): void {
    this.expandedRowIds.set(this.rows().map((row) => row.id));
  }

  public collapseAllRows(): void {
    this.expandedRowIds.set([]);
  }

  public highlight(rowId: string | null, columnId: string | null): void {
    this.hoveredRowId.set(rowId);
    this.hoveredColumnId.set(columnId);
  }

  private readonly unique = (id: string, index: number, ids: string[]): boolean => ids.indexOf(id) === index;

  /**
   * Showing a column removes the button that was used (stub, "Show all"): focus goes to the column's header instead.
   * The header cell stays in the DOM either way (only its content changes), so it takes focus at once.
   */
  private focusHeader(columnId: string): void {
    this.host.nativeElement
      .querySelector<HTMLElement>(`[role="columnheader"][data-column-id="${CSS.escape(columnId)}"]`)
      ?.focus();
  }
}
